/**
 * @Module AuthNFallbackPolicyType
 */
import {
  cloneDeep, get, isEmpty, pick,
} from 'lodash';

import { AuthnPolicyConsts } from 'client/util/Constants';
import { EffectAction } from 'client/Security/AuthNPolicyType';

const { policyActions } = AuthnPolicyConsts;

export const GlobalAuthPolicyConsts = {
  userPortal: 'userportal',
  application: 'application',
  ldap: 'ldap',
};

export const apiTypeDefaultValue = new Map([
  [GlobalAuthPolicyConsts.userPortal, 'legacy'],
  [GlobalAuthPolicyConsts.application, 'allow'],
  [GlobalAuthPolicyConsts.ldap, 'allow'],
]);

// Add legacy effect action - only valid for UP fallback policies
// Add unknown as placeholder since default action varies by resource type
EffectAction
  .set(policyActions.legacy, 'legacy')
  .set(policyActions.unknown, 'unknown');

const defaultFallbackPolicy = (resource) => {
  const action = apiTypeDefaultValue.get(resource) || policyActions.unknown;

  return {
    effect: {
      action: action.toLowerCase(),
      obligations: {
        mfa: {
          required: false,
        },
      },
    },
  };
};

export const formatAuthNFallbackPolicyForApi = policyItem => {
  const policyUpdate = cloneDeep(policyItem);

  const action = get(policyItem, 'effect.action');

  // 'allowWithMFA' is only a valid action client side
  // it must be converted to 'allow' with correct obligations
  if (action === policyActions.allowWithMFA) {
    policyUpdate.effect.action = policyActions.allow;
  }

  return pick(policyUpdate, 'effect');
};

export default function AuthNFallbackPolicy(
  args = {
    resource: undefined,
    effect: {
      action: policyActions.unknown,
    },
  },
  defaultPolicy = defaultFallbackPolicy(args.resource),
) {
  const data = {
    ...defaultPolicy,
    ...args,
  };

  const getAction = (action) => {
    const actionType = EffectAction.get(action) || EffectAction.get(policyActions.unknown);
    const isMFARequired = get(data, 'effect.obligations.mfa.required', false);

    return actionType === policyActions.allow && isMFARequired
      ? policyActions.allowWithMFA
      : actionType;
  };

  let resource;

  if (args.resource && args.resource.length > 0) {
    resource = Symbol(args.resource);
    this[resource] = resource.description;
  }

  this.effect = {};

  // The args come from the AuthNFallbackPolicyApi response
  // 404 args will be { resource: <value> }
  // 200 args will be { effect: {...} resource: <value> }
  // isDefault dictates which ITEM_STATE to set on the model
  // determining what type of request we need to make (POST or PUT)
  // true --> { create: true, update: false } (POST)
  // false --> { create: false, update true } (PUT)
  // See ConditionalFallbackPolicyModelFactory.js
  this.isDefault = isEmpty(args.effect);

  this.effect.action = getAction(data.effect.action);

  // Obligations can only be present if "action" is "allow" or "allowwithmfa".
  // If "action" is "allow" obligations is only required if obligations.mfa.required = true.
  // If obligations is present it must be an object in the form:
  // {
  //   mfa: {
  //     required: Boolean
  //   }
  // }
  if (
    (this.effect.action === policyActions.allow
    || this.effect.action === policyActions.allowWithMFA)
    && data.effect.obligations !== undefined
  ) {
    this.effect.obligations = data.effect.obligations;
  }

  return this;
}
